import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import { CheckCircle, Star, Users } from 'lucide-react';

const CloudEngineerProjectsPage = () => {
  const [expandedModules, setExpandedModules] = useState({});

  const toggleModule = (index) => {
    setExpandedModules(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const courseModules = [
    {
      title: "Introduction",
      lessons: [
        "Welcome",
        "Getting Technical Support for these 5 Projects [Slack Community]",
        "Course Introduction",
        "Project Costs Breakdown"
      ]
    },
    {
      title: "Project 1: On-Premise Application Migration to AWS",
      lessons: [
        "Project Overview",
        "Setting Up the Local 'On-Prem' Application",
        "Create a Simulated On-Prem Server on EC2",
        "Set Up Amazon RDS (Target Database)",
        "Set Up AWS DMS for Database Migration",
        "Cutover: Point the Application to Amazon RDS",
        "Conclusion and Cleanup"
      ]
    },
    {
      title: "Project 2: Blue Green and Rolling Infrastructure Deployments",
      lessons: [
        "Project Overview",
        "Prerequisites & Environment Setup",
        "Build the Base Infrastructure",
        "Implement Blue-Green Deployment",
        "Implement Rolling Deployments",
        "Testing the Deployments",
        "Conclusion & Cleanup"
      ]
    },
    {
      title: "Project 3: Multi-VPC Networking and PrivateLink Architecture",
      lessons: [
        "Project Overview",
        "Build and Configure All Three VPCs",
        "Deploy the Internal Service in the Shared Services VPC",
        "Set Up the PrivateLink Provider",
        "Consume the PrivateLink Service",
        "Understanding the Architecture & Final Testing",
        "Conclusion and Cleanup"
      ]
    },
    {
      title: "Project 4: Event-Driven Order Processing System on AWS",
      lessons: [
        "Project Overview",
        "Build the Order Ingestion Layer & Frontend Web App",
        "Real-Time Order Processing with DynamoDB Streams",
        "Add Reliability with SQS, Retries & DLQs",
        "Intelligent Event Routing with EventBridge Pipes",
        "Observability and Alerts",
        "Conclusion & Cleanup"
      ]
    },
    {
      title: "Project 5: Global Accelerator-Based Multi-Region Failover on AWS",
      lessons: [
        "Project Overview",
        "Architecture & Prerequisites",
        "Build the Primary Region Web Stack (Region A)",
        "Build the Secondary Region Web Stack (Region B)",
        "Configure Route 53 Health Checks & AWS Global Accelerator",
        "Test the Regional Failover",
        "Conclusion & Cleanup"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PromoBanner />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Course Info */}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-cyan-600 mb-6">
                  5 AWS Projects to Become a Cloud Engineer
                </h1>
                
                <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                  Complete Step-By-Step Guide to five AWS Projects to help you get hired as a Cloud Engineer.
                </p>
                
                <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                  Build these high-quality projects, and add them to your portfolio!
                </p>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  These projects are designed to boost your Cloud experience and help you build Cloud Engineer skills.
                </p>
                
                <div className="flex items-center gap-2 mb-8">
                  <Users size={20} className="text-blue-500" />
                  <span className="text-lg font-medium text-gray-700">Join Cloud Engineers worldwide!</span>
                  <Star size={20} className="text-yellow-500 fill-current" />
                </div>
                
                <a 
                  href="https://learn.zerotocloud.co/p/5-projects-to-become-a-cloud-engineer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block text-center"
                >
                  Enroll now for $69
                </a>
              </div>
              
              {/* Right Side - Course Thumbnail */}
              <div className="relative">
                <img 
                  src="/course-covers/cloud-engineer-projects.png"
                  alt="5 AWS Projects to Become a Cloud Engineer"
                  className="w-full rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-cyan-600 mb-12 text-center">
              Course Content
            </h2>
            
            <div className="space-y-4">
              {courseModules.map((module, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                  <button
                    onClick={() => toggleModule(index)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-all duration-200 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-blue-600 font-semibold text-lg">
                        {index + 1}.
                      </span>
                      <h3 className="text-gray-900 font-medium">
                        {module.title}
                      </h3>
                    </div>
                    <div className="text-blue-600">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className={`transform transition-transform duration-200 ${expandedModules[index] ? 'rotate-90' : ''}`}>
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </button>
                  
                  {expandedModules[index] && (
                    <div className="px-4 pb-4 bg-gray-50 border-t border-gray-200">
                      <ul className="space-y-3 pt-4">
                        {module.lessons.map((lesson, lessonIndex) => (
                          <li key={lessonIndex} className="flex items-start gap-3">
                            <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                            <span className="text-gray-700">{lesson}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Start Learning Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-cyan-600 mb-6">
              Ready to start learning?
            </h2>
            
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="mb-6">
                <div className="text-4xl font-bold text-gray-900 mb-2">$69</div>
                <p className="text-gray-600">One-time payment • Lifetime access</p>
              </div>
              
              <a 
                href="https://learn.zerotocloud.co/p/5-projects-to-become-a-cloud-engineer"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-xl hover:shadow-lg transition-all duration-200 mb-4 inline-block text-center"
              >
                Enroll now for $69
              </a>
              
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Users size={16} className="text-blue-500" />
                <span>Launch your Cloud Engineering career today</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CloudEngineerProjectsPage;
